import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { Card, ListGroup } from 'react-bootstrap';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { useLocation } from 'react-router-dom'; // <-- ADDED for reading URL query

const ReviewSubmissions = () => {
  const reviewerID = localStorage.getItem('reviewerID');
  const platformID = localStorage.getItem('platformID');
  const [submissions, setSubmissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [decision, setDecision] = useState('');
  const [comment, setComment] = useState('');
  const [fullPaperChat, setFullPaperChat] = useState([]);
  const [isParent, setIsParent] = useState(false);
  const [commentBox, setCommentBox] = useState(false);
  const [isAcceptedRejected, setIsAcceptedRejected] = useState(false);
  const [isReply, setIsReply] = useState(true);

  // <-- NEW: Use query string to set default filter
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialFilter = queryParams.get('filter') || 'all';
  const [filterStatus, setFilterStatus] = useState(initialFilter); // <-- UPDATED

  const API = process.env.REACT_APP_API_BASE_URL;

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    toolbarPlugin: {
      toolbarSlot: (slots) => {
        const { Download, ...rest } = slots;
        return { ...rest };
      },
    },
  });

  useEffect(() => {
    if (reviewerID) {
      axios
        .get(`${API}/api/review-submissions/${reviewerID}`)
        .then((res) => {
          setSubmissions(res.data);
          // console.log('ss', res.data);
        })
        .catch((err) => console.error('Error fetching submissions:', err));
    }
  }, [reviewerID]);

  const handleSubmit = async () => {
    if (!decision) return alert('Please select a decision.');
    try {
      await axios.post(`${API}/api/review-submissions/review`, {
        AuthorID: selected.authorID,
        submissionID: selected.submissionID,
        ReviewerID: reviewerID,
        Decision: decision,
        Comment: comment,
        PlatformID: selected.PlatformID || platformID,
      });
      alert('Review submitted!');
      setSelected(null);
      setDecision('');
      setComment('');
      const res = await axios.get(`${API}/api/review-submissions/${reviewerID}`);
      setSubmissions(res.data);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err.message ||
        'Unexpected error occurred';
      alert(msg);
    }
  };

  const handleReview = async (s) => {
    try {
      setSelected(s);
      const { submissionID, authorID } = s;

      const res = await axios.get(
        `${API}/api/review-submissions/${platformID}/${reviewerID}/${submissionID}/${authorID}`
      );

      setFullPaperChat(res.data.FullPaperChat);

      if (res.data.FullPaperChat.length) {
        const lastSubmission = res.data.FullPaperChat[res.data.FullPaperChat.length - 1];
        setIsReply(lastSubmission.AuthorReply ? true : false);
      }

      setIsParent(res.data.isParent);
      setIsAcceptedRejected(res.data.hasDecision);
    } catch (err) {
      console.error("Error fetching review data:", err);
      alert("An error occurred while fetching review data.");
    }
  };

  return (
    <div className="container mt-4 ml-2">
      {selected && (
        <div className="mt-5">
          <h5 className="fs-4 mb-3">Reviewing: <i className="fw-bold">{selected.SubmissionTitle}</i></h5>
          {isAcceptedRejected ? (
            <div className="alert alert-dark fw-bold text-center mb-3 rounded shadow-sm mt-3" role="alert">
              You have already been reviewed.
            </div>
          ) : null}
          <div className="row g-4">
            {/* PDF Viewer */}
            <div className="col-md-6">
              <Card className="shadow-sm">
                <Card.Header className="fw-semibold bg-light">Submitted Paper</Card.Header>
                <Card.Body style={{ height: '600px', overflow: 'hidden' }}>
                  {selected.File.endsWith('.pdf') ? (
                    <Worker workerUrl="/pdf.worker.min.js">
                      <Viewer
                        fileUrl={`${API}/uploads/papers/${selected.File}`}
                        plugins={[defaultLayoutPluginInstance]}
                      />
                    </Worker>
                  ) : (
                    <div className="alert alert-warning">File is not a valid PDF</div>
                  )}
                </Card.Body>
              </Card>
            </div>

            {/* Right Column - Decisions and Chat */}
            <div className="col-md-6">
              {!isParent && !isAcceptedRejected && (
                <Card className="shadow-sm mb-3">
                  <Card.Header className="fw-semibold bg-light">Decision</Card.Header>
                  <Card.Body>
                    {/* Decision options */}
                    <div className="d-flex flex-wrap gap-4 mb-2">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          value="Accept"
                          checked={decision === 'Accept'}
                          onChange={(e) => {
                            setDecision(e.target.value);
                            setCommentBox(false);
                            setComment('');
                          }}
                        />
                        <label className="form-check-label">Accept</label>
                      </div>

                      {!(selected.SubmissionTitle.includes("Abstract") || selected.SubmissionTitle.includes("Revision")) && (
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            value="AcceptWithComment"
                            checked={decision === 'AcceptWithComment'}
                            onChange={(e) => {
                              setDecision(e.target.value);
                              setCommentBox(true);
                            }}
                          />
                          <label className="form-check-label">Comment</label>
                        </div>
                      )}
                    </div>

                    <div className="d-flex flex-wrap gap-4 mb-3">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          value="Reject"
                          checked={decision === 'Reject'}
                          onChange={(e) => {
                            setDecision(e.target.value);
                            setCommentBox(false);
                            setComment('');
                          }}
                        />
                        <label className="form-check-label">Reject</label>
                      </div>
                    </div>

                    {commentBox && (
                      <div className="form-group mb-3">
                        <label className="fw-bold">Comment</label>
                        <textarea
                          className="form-control"
                          rows="4"
                          value={comment}
                          onChange={(e) => setComment(e.target.value)}
                        />
                        <p> You can enlarge the comment box by holding the bottom-right corner and dragging down.</p>
                      </div>
                    )}

                    <div className="d-flex gap-2">
                      <button className="btn btn-success" onClick={handleSubmit}>Submit Decision</button>
                      <button className="btn btn-outline-secondary" onClick={() => setSelected(null)}>Cancel</button>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Chat History */}
              <Card className="shadow-sm">
                <Card.Header className="fw-semibold bg-light">Conversation History</Card.Header>
                <Card.Body>
                  {fullPaperChat.length === 0 ? (
                    <p className="text-muted">No previous messages available.</p>
                  ) : (
                    <ListGroup variant="flush">
                      <hr />
                      <h4 className='text-center mb-4'>Full Paper Chat</h4>
                      <hr />
                      {fullPaperChat.map((c, index) => (
                        c.Comment && (
                          <ListGroup.Item key={index} className="px-3 py-2">
                            <div className="row">
                              <div className="col-md-6 mb-2">
                                <strong className="text-primary">Reviewer:</strong>
                                <div>{c.Comment}</div>
                              </div>
                              <div className="col-md-6 mb-2">
                                <strong className="text-success">{c.AuthorName || 'Author'}:</strong>
                                <div>{c.AuthorReply || <em>No reply</em>}</div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        )
                      ))}
                    </ListGroup>
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>
        </div>
      )}

      {/* Filter Dropdown */}
      <div className="mt-5">
        <h3 className="page-title mb-4">Assigned Submissions</h3>
        <div className="mb-3 d-flex align-items-center gap-3">
          <label htmlFor="filter" className="fw-semibold mb-0">Filter:</label>
          <select
            id="filter"
            className="form-select w-auto"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All</option>
            <option value="notReviewed">Not Reviewed</option>
            <option value="reviewed">Reviewed</option>
          </select>
        </div>

        {/* Submissions Table */}
        <table className="table table-hover table-bordered">
          <thead className="table-light">
            <tr>
              <th>Title</th>
              <th hidden>Author</th>
              <th hidden>Email</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {submissions
              // 1. Sort by ReviewDate (latest first)
              .sort((a, b) => new Date(b.ReviewDate) - new Date(a.ReviewDate))

              // 2. Deduplicate by submissionID + ReviewerID to get only latest review per submission per reviewer
              .filter((item, index, self) =>
                index === self.findIndex(
                  (s) => s.submissionID === item.submissionID && s.ReviewerID === item.ReviewerID
                )
              )

              // 3. Apply filter status (based on final decision only)
              .filter((s) => {
                const finalDecision = s.Decision;

                if (filterStatus === 'reviewed') {
                  // Reviewed only if final decision is Accept or Reject
                  return finalDecision === 'Accept' || finalDecision === 'Reject';
                }

                if (filterStatus === 'notReviewed') {
                  // Not reviewed if no decision or only AcceptWithComment (i.e. still waiting for final Accept/Reject)
                  return !finalDecision || finalDecision === 'AcceptWithComment';
                }

                return true; // Show all
              })

              // 4. Render table rows
              .map((s) => (
                <tr key={s.Srid ?? `${s.submissionID}-${s.authorID}`}>
                  <td>{s.SubmissionTitle}</td>
                  <td hidden>{s.AuthorName}</td>
                  <td hidden>{s.EmailID}</td>
                  <td>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={() => handleReview(s)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReviewSubmissions;
